<template>
  <div id="gantt-container" class="d-flex flex-column">
    <div style="min-height: 4px">
      <v-progress-linear
        :active="showProgressbar"
        indeterminate
        color="primary"
        height="4"
        class="mb-0">
      </v-progress-linear>
    </div>

    <div class="flex-grow-1 mb-2 pe-3">
      <div
        id="gantt-mount"
        :class="$vuetify.theme.isDark ? 'gantt-dark-theme' : ''"
        ref="ganttContainer"
        style="width: 100%; height: 100%"></div>
    </div>
    <v-list
      v-if="showTaskActionsMenu"
      v-click-outside="{
        handler: closeActionsMenu,
      }"
      :style="{ position: 'absolute', left: menuLeft, top: menuTop }"
      class="bg2 gantt-more-options py-0"
      elevation="2"
      width="180px"
      dense>
      <v-list-item @click="createNewTask(gantt.getSelectedId())" v-has="permissions.Tasks.Create">
        <v-list-item-icon>
          <v-icon>mdi-plus</v-icon>
        </v-list-item-icon>
        <v-list-item-title>Add Task</v-list-item-title>
      </v-list-item>
      <v-list-item @click="showLightbox(gantt.getSelectedId())" v-has="permissions.Tasks.Update">
        <v-list-item-icon>
          <v-icon>mdi-pencil</v-icon>
        </v-list-item-icon>
        <v-list-item-title>Edit Task</v-list-item-title>
      </v-list-item>
      <v-list-item v-has="permissions.Tasks.Delete" @click="deleteTask(gantt.getSelectedId())">
        <v-list-item-icon>
          <v-icon>mdi-delete</v-icon>
        </v-list-item-icon>
        <v-list-item-title>Delete Task</v-list-item-title>
      </v-list-item>
    </v-list>
    <EditTask ref="editTask" @onEdit="onEditTask($event)" />
    <EditProject ref="editProject" @onProjectUpdate="handleOnProjectUpdate"></EditProject>
    <EditContract ref="editContract" @onContractUpdate="handleOnContractUpdate"></EditContract>
    <FacilitySelectDialog ref="facilityDialog" />
  </div>
</template>

<script>
  // @workflow tasks.show-in-3d
  var gantt = window.gantt;
  import EditTask from '@/features/Task/components/EditTask.vue';
  import EditProject from '@Contract/components/EditProject.vue';
  import EditContract from '@Contract/components/EditContract.vue';
  import { mapGetters } from 'vuex';
  import { $clean, $has } from '@/utils';
  import { GanttFeatures } from '@Task/config/gantt-features';
  import ganttService from '@Task/service/gantt.service';
  //import { TaskGroupProps } from '@/features/Share/config/enums';
  import permissions from '@Share/config/permissions';
  import { log, warn, error as debugError } from '@/utils/debugLogger';
  import { copyTaskLinkToClipboard } from '@/utils/shareIntents';
  import {
    getTaskIsolationInfo,
    getCopyLinkDirectTarget,
  } from '@Task/utils/taskIsolation';
  import { isolateTask } from '@/actions/tasks';
  import { CAPABILITY, can } from '@/actions/permissions';
  import FacilitySelectDialog from '@Task/components/FacilitySelectDialog.vue';
  // import GanttUtils from '@/utils/gantt-utils'
  import {
    histogram_cell_label_template,
    histogram_cell_allocated_template,
    histogram_cell_capacity_template,
    histogram_cell_class_template,
    resource_cell_value_template,
    resource_cell_class_template,
    timeline_cell_class_template,
    week_scale_template,
    task_cell_class_template,
    ganttZoomConfig,
    ganttLayoutConfig,
    ganttColumnsConfig,
    ganttSelectionHeader,
    gantt_selection_column_template,
    actions_template,
    showIn3d_template,
    copyLink_template,
  } from '../config/gantt-templates';
  export default {
    name: 'app-gantt',
    data() {
      return {
        permissions,
        gantt,
        ganttFeatures: {},
        ganttEvents: [],
        ganttOptions: {
          criticalPath: false,
          FitToScale: false,
          scale: '',
          autoScheduling: false,
        },

        showTaskActionsMenu: false,
        menuLeft: '0px',
        menuTop: '0px',
        cachedSettings: {},

        zoomConfig: ganttZoomConfig,
        collectionSelected: false,
        todayMarkerId: null,
        groups: null,
        loading: false,
        entries: {
          tasks: { items: [], total: 0 },
          links: [],
          groupByKeys: [],
          groups: null,
        },
      };
    },
    props: {
      ganttToolbar: {
        type: Array,
        default: () => [],
      },
      selectedScaleOption: {
        type: String,
        default: () => 'day',
      },
    },
    computed: {
      /** Show tasks in 3D (row button): the permission map (actions/permissions.js). */
      canShowTasksIn3d() {
        return can(CAPABILITY.TASKS_SHOW_IN_3D, this.$store);
      },
      showProgressbar() {
        return this.loading || this.linkUpdating || this.updateLoading;
      },
      /**
       * Facility dictionary (id → name) from store for cross-asset isolation.
       */
      facilityDict() {
        return this.$store.getters.getFacilityDict || {};
      },
      ...mapGetters('TASK', {
        updateLoading: 'GET_UPDATE_LOADING',
      }),
      ...mapGetters('LINK', {
        linkUpdating: 'GET_UPDATE_LOADING',
      }),
      ...mapGetters('USER', {
        users: 'GET_ENTRIES',
        linkUpdating: 'GET_UPDATE_LOADING',
      }),
      ...mapGetters('PROJECT', {
        projects: 'GET_ENTRIES',
      }),
      ...mapGetters('TASKSTATUS', {
        taskStatuses: 'GET_ENTRIES',
      }),
      ...mapGetters('TASK_MANAGER', {
        taskTypes: 'GET_ENTRIES',
      }),
      options: {
        get() {
          return this.$store.getters['TASK/GET_OPTIONS'];
        },
        set(val) {
          this.$store.commit('TASK/SET_OPTIONS', val);
        },
        taskType() {
          this.taskTypes.find((e) => e.id == this.$route.params.taskTypeId);
        },
      },
      highlightCriticalPath: {
        get() {
          return this.$store.getters['GANTT/GET_HIGHLIGHT_CRITICAL_PATH'];
        },
        set(val) {
          this.$store.commit('GANTT/SET_HIGHLIGHT_CRITICAL_PATH', val);
        },
      },
      clickEventId: {
        get() {
          return this.$store.getters['GANTT/GET_CLICK_EVENT_ID'];
        },
        set(val) {
          this.$store.commit('GANTT/SET_CLICK_EVENT_ID', val);
        },
      },
      tasks() {
        return {
          data: this.entries.tasks?.items,
          links: this.entries.links,
          resources:
            this.users.map((e) => ({ id: e.id, text: e.firstName, unit: 'hours/day' })) || [],
        };
      },
      refreshGant: {
        get() {
          return this.$store.getters['GANTT/GET_RENDER_GANTT'];
        },
      },
      selectedTasks: {
        get() {
          return this.$store.getters['GANTT/GET_SELECTED_TASKS'];
        },
        set(val) {
          this.$store.commit('GANTT/SET_SELECTED_TASKS', val);
        },
      },
      routeToTaskFired: {
        get() {
          return this.$store.getters['TASK/GET_ROUTE_TO_PATH_FIRED'];
        },
        set(val) {
          this.$store.commit('TASK/SET_ROUTE_TO_PATH_FIRED', val);
        },
      },
      taskToRouteId: {
        get() {
          return this.$store.getters['TASK/GET_TASK_TO_ROUTE_ID'];
        },
      },
    },
    async mounted() {
      this.ganttConfig();
      this.addGanttPlugins();
      let features = new GanttFeatures();
      features.enableAll();
      gantt.init(this.$refs.ganttContainer);
      gantt.createDataProcessor((entity, action, data, id) => {
        let ev = {
          entity,
          action,
          data,
          id,
        };
        this.handleGanttEvent(ev);
      });

      this.displayGantt();
      
      // Load data after gantt is initialized - entries watcher will handle rendering
      await this.loadGanttData();

      window.addEventListener('resize', this.adjustGanttHeight);
    },
    deactivated() {
      gantt?.ext?.tooltips?.hideTooltip();
    },
    async activated() {
      this.ganttAttachTaskClickEvents();
      this.showCustomColumns(true);

      this.ganttConfig();
      this.addGanttPlugins();
      let features = new GanttFeatures();
      features.enableAll();
      this.displayGantt();
      
      // Load data - entries watcher will handle rendering
      await this.loadGanttData();
    },
    methods: {
      async loadGanttData() {
        this.loading = true;
        try {
          const options = this.$store.getters['TASK/GET_OPTIONS'];
          const params = {
            ...options,
            typeId: this.$route.params.taskTypeId,
            sortBy: 'GanttOrder',
          };
          
          const response = await ganttService.getGantt(params);
          const data = response.data || {
            tasks: { items: [], total: 0 },
            links: [],
            groupByKeys: [],
            groups: null,
          };
          
          // Map task types to convert dates and flatten custom fields
          if (data.tasks?.items) {
            data.tasks.items = data.tasks.items.map(task => this.mapType(task));
          }
          
          // Set entries - the watcher will handle gantt rendering
          this.entries = data;
        } catch (err) {
          debugError('Gantt', 'Failed to load gantt data', err);
          this.$dialog.notify.error('Failed to load gantt data');
        } finally {
          this.loading = false;
        }
      },
      mapType(task) {
        task.start_date = new Date(task.start_date);
        task.end_date = new Date(task.end_date);

        if (task.customFields && task.customFields.length) {
          task.customFields.forEach(e => {
            task[e.fieldId] = e.value;
          });
        }
        return task;
      },
      deleteSelectedTasks() {
        var ids = this.tasks.data.filter((e) => e.$selected).map((e) => e.id);
        let text =
          ids.length > 1
            ? 'Are you sure you want to delete the task and its subtasks?'
            : 'Are you sure you want to delete the task?';

        this.$dialog.error({
          text: text,
          title: `Delete Tasks`,
          actions: {
            false: 'Cancel',
            true: {
              text: 'Ok',
              color: 'primary',
              handle: async () => {
                await this.$store.dispatch('TASK/DELETE', ids);
                
                this.collectionSelected = this.tasks.data.some((e) => e.$selected);

                gantt.hideLightbox();

                await this.loadGanttData();
              },
            },
          },
        });
      },
      onGanttRender() {
        let element = document.getElementById('tasks-select-header');
        if (element) element.onclick = this.onTaskSelectHeader;
      },
      onGanttReady() {
        const radios = [].slice.call(gantt.$container.querySelectorAll("input[type='radio']"));
        radios.forEach(function (r) {
          gantt.event(r, 'change', function () {
            const radios = [].slice.call(gantt.$container.querySelectorAll("input[type='radio']"));
            radios.forEach(function (r) {
              r.parentNode.className = r.parentNode.className.replace('active', '');
            });
            if (this.checked) {
              gantt.$resourceMode = this.value;
              this.parentNode.className += ' active';

              gantt.getDatastore(gantt.config.resource_store).refresh();
            }
          });
        });
      },
      onTaskSelectHeader(e) {
        let input = e.target;
        if (input.checked) {
          gantt.eachTask((e) => {
            e.$selected = true;
            gantt.selectTask(e.id);
          });
        } else {
          gantt.eachTask((e) => {
            e.$selected = false;
            gantt.unselectTask(e.id);
          });
        }
        this.collectionSelected = this.tasks.data.some((e) => e.$selected);
      },

      saveConfig() {
        var config = gantt.config;
        this.cachedSettings = {};
        this.cachedSettings.scales = config.scales;
        this.cachedSettings.start_date = config.start_date;
        this.cachedSettings.end_date = config.end_date;
        this.cachedSettings.scroll_position = gantt.getScrollState();
      },
      restoreConfig() {
        this.applyConfig(this.cachedSettings);
      },
      zoomToFit() {
        var project = gantt.getSubtaskDates(),
          areaWidth = gantt.$task.offsetWidth,
          scaleConfigs = this.zoomConfig.levels;

        for (var i = 0; i < scaleConfigs.length; i++) {
          var columnCount = this.getUnitsBetween(
            project.start_date,
            project.end_date,
            scaleConfigs[i].scales[scaleConfigs[i].scales.length - 1].unit,
            scaleConfigs[i].scales[0].step,
          );
          if ((columnCount + 2) * gantt.config.min_column_width <= areaWidth) {
            break;
          }
        }

        if (i == scaleConfigs.length) {
          i--;
        }

        gantt.ext.zoom.setLevel(scaleConfigs[i].name);

        this.applyConfig(scaleConfigs[i], project);
      },

      getUnitsBetween(from, to, unit, step) {
        var start = new Date(from),
          end = new Date(to);
        var units = 0;
        while (start.valueOf() < end.valueOf()) {
          units++;
          start = gantt.date.add(start, step, unit);
        }
        return units;
      },
      applyConfig(config) {
        gantt.config.scales = config.scales;

        // restore the previous scroll position
        if (config.scroll_position) {
          setTimeout(function () {
            gantt.scrollTo(config.scroll_position.x, config.scroll_position.y);
          }, 4);
        }
      },
      showCustomColumns(force = false) {
        let taskType = this.taskTypes.find((e) => e.id == this.$route.params.taskTypeId);
        if (!taskType) return;

        if (gantt.customColumnsInit == taskType.id && !force) return;
        gantt.customColumnsInit = taskType.id;

        var columns = taskType.sections
          .map((e) => e.fields)
          .flat()
          .filter((e) => e.isVisible)
          .map((e) => ({ name: e.id, label: e.label, width: '*', resize: true }));
        gantt.config.columns = [
          {
            name: 'select',
            label: ganttSelectionHeader,
            max_width: 40,
            template: gantt_selection_column_template,
            sort: false,
          },
          { name: 'text', label: 'Task name', tree: true, width: 350, resize: true },
          {
            name: 'start_date',
            label: 'Start time',
            align: 'center',
            width: 100,
            hide: true,
            resize: true,
          },
          { name: 'end_date', label: 'End time', align: 'center', width: 100, hide: true },
          { name: 'duration', label: 'Duration', align: 'center', width: 100, hide: true },
          ...columns,
          {
            name: 'show-in-3d',
            label: '3D',
            width: 50,
            template: showIn3d_template,
            resize: true,
            sort: false,
          },
          {
            name: 'copy-link',
            label: 'Link',
            width: 50,
            template: copyLink_template,
            sort: false,
          },
          {
            name: 'custom-actions',
            label: 'Actions',
            max_width: 80,
            template: actions_template,
            sort: false,
          },
        ];
        this.retrieveFromStorage(false);
        gantt.render();
      },
      onEditTask(payLoad) {
        this.$emit('edit-completed', payLoad);
      },
      handleOnProjectUpdate(project) {
        var entities = gantt.getTaskBy(function (task) {
          return task.key && task.key.split('.').reverse()[0] == project.id;
        });
        var group = this.groups.find((g) => g.key.split('.').reverse()[0] == project.id);

        group.label = project.description;

        entities.forEach((entity) => {
          entity.label = project.description;
          // gantt.updateTask(entity.id,entity);
          //     setTimeout(()=>{
          //       gantt.$data.tasksStore.refresh(entity.key);
          //       gantt._quickRefresh(function () {
          //   gantt.refreshTask(entity.key);
          // });
          //     })
        });

        this.groupTasks();
      },
      handleOnContractUpdate(contract) {
        var task = gantt.getTask(contract.id);
        Object.assign(task, contract);
        gantt.updateTask(contract.id, task);
      },
      onDiscard(taskId) {
        var task = gantt.getTask(taskId);
        if (task.$new) gantt.deleteTask(taskId);
      },
      deleteTask(taskId) {
        this.closeActionsMenu();

        let text = gantt.getChildren(taskId).length
          ? 'Are you sure you want to delete the task and its subtasks?'
          : 'Are you sure you want to delete the task?';

        this.$dialog.error({
          text: text,
          title: `Delete Tasks`,
          actions: {
            false: 'Cancel',
            true: {
              text: 'Ok',
              color: 'primary',
              handle: () => {
                gantt.deleteTask(taskId);
                gantt.hideLightbox();
              },
            },
          },
        });
      },
      openTask(task, isNew) {
        let data = {
          task,
          isNew,
          taskTypeId: this.$route.params.taskTypeId,
        };
        this.$refs.editTask.open(data);
      },
      handleGanttEvent(ev) {
        if (ev.entity == 'link') {
          this.handleLinksEvent(ev);
        } else if (ev.entity == 'task') {
          this.handleTasksEvent(ev);
        }
      },
      handleLinksEvent(ev) {
        let link = ev.data;
        switch (ev.action) {
          case 'create':
            this.$store.dispatch('LINK/ADD', link);
            break;

          case 'update':
            this.$store.dispatch('LINK/UPDATE', link);
            break;

          case 'delete':
            this.$store.dispatch('LINK/DELETE', link.id);
            break;
          default:
            break;
        }
      },

      handleTasksEvent(ev) {
        if (!gantt.isTaskExists(ev.data.id)) return;

        if (ev.data.app_handled) {
          let task = gantt.getTask(ev.data.id);
          ev.data.app_handled = false;
          if (task) {
            task.app_handled = false;
          }
          return;
        }

        ev.data.app_handled = false;
        let task = $clean(structuredClone(ev.data));
        if (ev.action == 'update') {
          if (ev.data['!nativeeditor_status'] === 'order') return;
          this.$store.dispatch('TASK/UPDATE', task);
        }

        if (ev.action == 'delete') {
          this.$store.dispatch('TASK/DELETE', [task.id]);
        }
      },
      displayGantt() {
        gantt.isInitialized = true;
        this.addTodayMarker();
      },
      addTodayMarker() {
        setTimeout(() => {
          if (this.todayMarkerId) {
            gantt.deleteMarker(this.todayMarkerId);
          }
          this.todayMarkerId = gantt.addMarker({
            start_date: new Date(),
            css: 'today',
            text: 'Today',
            title: 'Today: ',
          });
        }, 10);
      },
      addGanttPlugins() {
        gantt.plugins({
          critical_path: true,
          // quick_info: true,
          marker: true,
          tooltip: true,
          keyboard_navigation: true,
          undo: true,
          fullscreen: true,
          auto_scheduling: false,
          drag_timeline: true,
          multiselect: true,
          click_drag: true,
          grouping: true,
        });
      },

      ganttConfig() {
        let self = this;

        gantt.templates.histogram_cell_class = histogram_cell_class_template;
        gantt.templates.histogram_cell_label = histogram_cell_label_template;
        gantt.templates.histogram_cell_allocated = histogram_cell_allocated_template;
        gantt.templates.histogram_cell_capacity = histogram_cell_capacity_template;
        gantt.templates.resource_cell_value = resource_cell_value_template;
        gantt.templates.resource_cell_class = resource_cell_class_template;
        gantt.templates.timeline_cell_class = timeline_cell_class_template;
        gantt.templates.timeline_cell_class = task_cell_class_template;

        gantt.config.resource_property = 'owner';
        gantt.config.highlight_critical_path = this.highlightCriticalPath;
        gantt.config.date_format = '%Y-%m-%d %H:%i';
        gantt.config.xml_date = '%Y-%m-%d %H:%i';
        gantt.config.show_tasks_outside_timescale = true;
        gantt.config.resize_rows = true;
        gantt.config.autoscroll = true;
        gantt.config.autoscroll_speed = 20;
        gantt.config.multiselect = true;
        gantt.config.order_branch = 'marker';
        gantt.config.order_branch_free = true;
        gantt.config.click_drag = {
          callback: self.onDragEnd,
        };

        gantt.config.fit_tasks = true;
        gantt.$zoomToFit = false;
        gantt.ext.zoom.init(this.zoomConfig);

        gantt.config.sort = true;
        gantt.config.work_time = false;
        gantt.config.duration_unit = 'hour'; //an hour
        gantt.config.duration_step = 1;
        gantt.config.round_dnd_dates = false;
        gantt.config.time_step = 60;
        gantt.config.scale_height = 20 * 3;
        gantt.$resourceMode = 'hours';

        gantt.config.scales = [
          { unit: 'month', step: 1, format: '%F, %Y' },
          { unit: 'week', step: 1, format: week_scale_template },
          { unit: 'day', step: 1, format: '%D, %d' },
        ];

        gantt.templates.tooltip_date_format = function (date) {
          return gantt.date.date_to_str('%Y-%m-%d')(date);
        };
        gantt.templates.xml_date = function (date) {
          try {
            return gantt.date.str_to_date(gantt.config.xml_date)(date);
          } catch (err) {
            debugError('Gantt', 'xml_date template error', err);
          }
        };

        gantt.config.columns = ganttColumnsConfig;

        this.retrieveFromStorage(true);

        gantt.showLightbox = function (id) {
          var task = gantt.getTask(id);
          self.openTask(task, task.$new);
        };

        gantt.config.type_renderers[gantt.config.types.project] = function (task) {
          var main_el = document.createElement('div');
          main_el.setAttribute(gantt.config.task_attribute, task.id);
          var size = gantt.getTaskPosition(task);
          main_el.innerHTML = [
            "<div class='project-left'></div>",
            "<div class='project-right'></div>",
          ].join('');
          main_el.className = 'custom-project';

          main_el.style.left = size.left + 'px';
          main_el.style.top = size.top + 7 + 'px';
          main_el.style.width = size.width + 'px';

          return main_el;
        };

        function getAbsolutePosition(taskId) {
          let tasks = gantt.serialize().data;
          let absoluteIndex = 0;

          for (let i = 0; i < tasks.length; i++) {
            if (tasks[i].id === taskId) {
              return absoluteIndex;
            }
            absoluteIndex++;
          }

          return -1;
        }

        const _this = this;
        gantt.attachEvent('onAfterTaskMove', function (id) {
          let targetAbsoluteIndex = getAbsolutePosition(id);
          _this.$store.dispatch('TASK/REORDER_GANTT_TASK', {
            taskId: id,
            order: targetAbsoluteIndex,
            typeId: _this.$route.params.taskTypeId,
          });
        });

        gantt.attachEvent('onAfterTaskUpdate', function (id) {
          let selected = gantt.$data.tasksStore
            .getItems()
            .filter((e) => e.$selected)
            .map((e) => e.id);

          gantt.getSelectedTasks().forEach((id) => {
            gantt._multiselect._selected[id] = selected.includes(id);
            gantt.refreshTask(id);
          });

          gantt._multiselect._selected[id] = true;
          gantt.refreshTask(id);
        });

        gantt.attachEvent('onTaskUnselected', function (id) {
          let item = gantt.$data.tasksStore.getItem(id);
          let selected = gantt._multiselect.isSelected(id);

          if (item?.$selected && !selected) {
            gantt._multiselect.select(id);
          }

          const checkbox = document.getElementById(id);
          if (checkbox) {
            checkbox.checked = !!item?.$selected;
          }
        });

        gantt.attachEvent('onColumnResizeEnd', function (index, column, new_width) {
          gantt.config.columns[index].width = new_width;
          let columnsWidthsArray = gantt.config.columns.map((element) => ({
            name: element.name,
            width: element.width,
          }));

          localStorage.setItem('ganttColumnWidths', JSON.stringify(columnsWidthsArray));
          gantt.render();
          return true;
        });
      },

      showLightbox(id) {
        this.closeActionsMenu();
        var task = gantt.getTask(id);
        this.openTask(task, task.$new);
      },
      createNewTask(parentId) {
        this.closeActionsMenu();
        gantt.createTask({}, parentId);
      },
      ganttOnTaskDblClick(id) {
        let task = gantt.getTask(id);
        // if (task.propertyName === 'Project.ContractId'&&$has(permissions.Contracts.View)) {
        //   this.$refs.editContract.open(task.$group_id.split('.').reverse()[0], true);
        // }
        if (task.propertyName === 'ProjectId') {
          var projId = task.$group_id.split('.').reverse()[0];

          if (projId.startsWith('empty')) return true;
          this.$refs.editProject.open(projId, true);
        }
        return true;
      },
      ganttOnTaskClicked(id, e) {
        let input = e.target.closest('[data-action]');
        let task = gantt.getTask(id);
        if (input) {
          let action = input.getAttribute('data-action');

          switch (action) {
            case 'isolate3d':
              if (this.canShowTasksIn3d) this.handleTaskIsolation(task);
              break;
            case 'task-select':
              this.selectTask(task, e.target);
              break;
            case 'copyLink':
              this.copyTaskLink(task);
              break;
            case 'showActionsMenu':
              this.handleShowActionsMenu(task, e);
          }
        }
        this.tasks.data
          .filter((e) => e.$selected)
          .forEach((task) => {
            let isChecked = document.querySelectorAll(
              `input[type="checkbox"][id="${task.id}"]`,
            ).checked;
            if (!isChecked) gantt.selectTask(task.id);
          });

        this.selectedTasks = gantt.$data.tasksStore
          .getItems()
          .filter((e) => e.$selected)
          .map((e) => e.id);

        //this.collectionSelected=this.entries.some(e=>e.$selected);
        return true;
      },
      ganttAttachTaskClickEvents() {
        var self = this;
        if (this.ganttEvents.length) {
          this.ganttEvents.forEach((e) => {
            gantt.detachEvent(e);
          });
        }
        this.ganttEvents = [];
        this.ganttEvents.push(gantt.attachEvent('onTaskClick', this.ganttOnTaskClicked));
        this.ganttEvents.push(gantt.attachEvent('onTaskDblClick', this.ganttOnTaskDblClick));
        this.ganttEvents.push(
          gantt.attachEvent('onGroupClick', function (group) {
            alert('Group Name: ' + group.label);
            return true;
          }),
        );
        

        this.ganttEvents.push(
          gantt.attachEvent('onBeforeTaskDelete', function () {
            if (!$has(permissions.Tasks.Delete)) {
              var text = "Error,You don't have permission to delete tasks";
              self.$dialog.error({
                text: text,
                title: `Error, Deleting Tasks`,
                actions: {
                  true: {
                    text: 'Ok',
                    color: 'primary',
                  },
                },
              });
            }
            return $has(permissions.Tasks.Delete);
          }),
        );

        this.ganttEvents.push(
          gantt.attachEvent('onLinkClick', function (id) {
            var link = this.getLink(id),
              src = this.getTask(link.source),
              trg = this.getTask(link.target),
              types = this.config.links;

            var first = '',
              second = '';
            switch (link.type) {
              case types.finish_to_start:
                first = 'finish';
                second = 'start';
                break;
              case types.start_to_start:
                first = 'start';
                second = 'start';
                break;
              case types.finish_to_finish:
                first = 'finish';
                second = 'finish';
                break;
            }

            gantt.message(
              'Must ' +
                first +
                ' <b>' +
                src.text +
                '</b> to ' +
                second +
                ' <b>' +
                trg.text +
                '</b>',
            );
          }),
        );
        this.ganttEvents.push(gantt.attachEvent('onGanttRender', this.onGanttRender));
        this.ganttEvents.push(gantt.attachEvent('onGanttReady', this.onGanttReady));
      },
      ganttAttachEvents() {},
      selectTask(item, e) {
        var state = e.checked;
        item.$selected = state;

        // Recursive function to process a task and all its descendants
        const processTaskAndDescendants = (taskId, selected) => {
          // Process the current task
          let task = gantt.getTask(taskId);
          if (task) {
            task.$selected = selected;
            if (selected) {
              gantt.selectTask(taskId);
            } else {
              gantt.unselectTask(taskId);
            }

            // Process all children recursively
            const children = gantt.getChildren(taskId);
            children.forEach((childId) => {
              processTaskAndDescendants(childId, selected);
            });
          }
        };

        // Process the current task and all its descendants
        processTaskAndDescendants(item.id, state);

        // Update the collection selected state
        this.collectionSelected = this.tasks.data.some((e) => e.$selected);
      },
      unselectTasks() {
        gantt.eachSelectedTask(function (item) {
          gantt.unselectTask(item.id);
        });
      },
      onDragEnd(startPoint, endPoint, startDate, endDate, tasksBetweenDates, tasksInRows) {
        var mode = 1;
        switch (mode) {
          case '1':
            this.unselectTasks();
            tasksBetweenDates.forEach(function (item) {
              gantt.selectTask(item.id);
            });
            break;
          case '2':
            this.unselectTasks();
            tasksInRows.forEach(function (item) {
              gantt.selectTask(item.id);
            });
            break;
          case '3':
            this.unselectTasks();
            for (var i = 0; i < tasksBetweenDates.length; i++) {
              for (var j = 0; j < tasksInRows.length; j++) {
                if (tasksBetweenDates[i] === tasksInRows[j]) {
                  gantt.selectTask(tasksBetweenDates[i].id);
                }
              }
            }
            break;
        }
      },
      /** A row's 3D button: the one show-tasks action (actions/tasks.js isolateTask). */
      handleTaskIsolation(task) {
        return isolateTask({ store: this.$store, router: this.$router }, task, {
          chooseFacility: (subject, info) => this.$refs.facilityDialog.open(subject, info, this.facilityDict),
        });
      },

      /**
       * Copy a deep-link URL for the task to the clipboard. Uses the
       * SHARE_INTENT registry's task-expand encoder so URL format stays in
       * sync with the decoder side.
       */
      /**
       * Copy a deep-link URL for a task. Applies the universal rule from the
       * copy-link feature plan:
       *   - if the task has tags in the current facility → task-expand intent
       *     (URL opens the 3D / 360 viewer with isolation)
       *   - else → task-board intent (URL opens the Kanban view; recipient
       *     sees the task as a card without entering the viewer)
       *
       * TODO(gantt-multi-facility-menu): when the task has tags in MULTIPLE
       * facilities, this currently picks the current facility and applies the
       * universal rule against it — it does NOT open a facility-selection
       * menu the way TaskCopyLinkButton does in Kanban / List. The Gantt UX
       * is grid-based and doesn't expose a card-style menu trigger; adding
       * one means a centralized v-menu in this view's template + repositioning
       * via click-event coordinates. Deferred per the plan; tracked here so
       * the next implementer can find it.
       */
      async copyTaskLink(task) {
        let info = null;
        try {
          info = await getTaskIsolationInfo(task, this.$store);
        } catch (err) {
          debugError('[Gantt.copyTaskLink] failed to load isolation info', err);
        }
        const currentAssetId = this.$store.state.assetId;
        // Fall back to a plain task-expand URL for current if isolation
        // lookup fails — preserves prior behavior in the unhappy path.
        const target =
          getCopyLinkDirectTarget(info, currentAssetId) || {
            facilityId: currentAssetId,
            intent: 'task-expand',
          };
        log('[Gantt.copyTaskLink]', {
          taskId: task.id,
          intent: target.intent,
          facilityId: target.facilityId,
        });
        copyTaskLinkToClipboard({
          facilityId: target.facilityId,
          taskId: task.id,
          taskTypeId: this.$route.params.taskTypeId,
          intent: target.intent,
          dialog: this.$dialog,
        });
      },

      handleShowActionsMenu(task, e) {
        let sideMenuWidth = document.getElementById('viewer-side-menu').offsetWidth;

        let x = e.clientX - sideMenuWidth + 15;
        let y = e.clientY;

        this.showTaskActionsMenu = !!task;
        this.menuLeft = x + 'px';
        this.menuTop = y + 'px';
      },
      groupByTree(items, groupByKey) {
        return items.reduce((tree, item) => {
          const group = item[groupByKey];
          if (!tree[group]) {
            tree[group] = [];
          }
          tree[group].push(item);
          return tree;
        }, {});
      },
      getGroupData() {
        return this.$store
          .dispatch('TASK/GET_GROUPS', { typeId: this.$route.params.taskTypeId })
          .then((e) => {
            let taskType = this.taskTypes.find((e) => e.id == this.$route.params.taskTypeId);
            if (!taskType || !this.tasks.data) return;
            if (!e.data.groups || !e.data.groups.length) return;
          });
      },
      groupTasks() {
        this.groups = this.entries.groups;
        if (this.groups == null) return;

        let groupConfig = {
          relation_property: 'groupBy',
          groups: this.groups,
          group_id: 'key',
          group_text: 'label',
          default_group_label: 'Not Assigned',
          save_tree_structure: true,
        };

        gantt.groupBy(groupConfig);
      },
      updateHighlightCriticalPath() {
        gantt.config.highlight_critical_path = this.highlightCriticalPath;
        gantt.render();
      },
      closeActionsMenu() {
        this.showTaskActionsMenu = false;
      },
      refresh() {
        setTimeout(() => {
          gantt.render();
          this.$store.commit('GANTT/SET_RENDER_GANTT', false);
        }, 100);
      },
      resizeListener() {
        const callback = () => {
          if (gantt) gantt.resetLayout();
        };

        const observer = new ResizeObserver(callback);

        const htmlDiv = document.getElementById('gantt-container');

        if (!htmlDiv) return;

        observer.observe(htmlDiv);
      },
      retrieveFromStorage(render = true) {
        // Retrieve column widths from localStorage
        const storedWidths = localStorage.getItem('ganttColumnWidths');

        if (storedWidths) {
          const parsedWidths = JSON.parse(storedWidths);
          parsedWidths.forEach((data) => {
            if (!data.name || !data.width) return;
            let column = gantt.config.columns.find((e) => e.name == data.name);

            if (column) column.width = data.width;
          });
          if (render) gantt.render();
        }
      },
      adjustGanttHeight() {
        this.ganttConfig();
        this.addGanttPlugins();
        let features = new GanttFeatures();
        features.enableAll();
        gantt.init(this.$refs.ganttContainer);
      },
      toggleResourcePanel(val) {
        gantt.resourcePanelVisible = val;
        if (val) {
          gantt.config.layout = ganttLayoutConfig(true);
        } else {
          gantt.config.layout = ganttLayoutConfig();
        }
        this.adjustGanttHeight();
      },
    },
    watch: {
      selectedScaleOption(val) {
        gantt.ext.zoom.setLevel(val);

        this.applyConfig(val);
      },
      ganttToolbar(val) {
        this.highlightCriticalPath = val.includes('highlightCriticalPath');
        var resourcePanelVisible = val.includes('resourcePanel');
        this.updateHighlightCriticalPath();
        if (resourcePanelVisible) {
          if (resourcePanelVisible != gantt.resourcePanelVisible) {
            this.toggleResourcePanel(resourcePanelVisible);
          }
        } else {
          if (gantt.resourcePanelVisible) {
            this.toggleResourcePanel(resourcePanelVisible);
          }
        }
      },
      taskTypes() {
        this.showCustomColumns();
      },
      projects() {},
      entries() {
        gantt.clearAll();
        this.showCustomColumns();
        var groupByKeys = this.entries.groupByKeys;

        if (!this.tasks.data) return;

        this.tasks.data.forEach((e) => {
          e.groupBy = groupByKeys?.find((key) => key.id == e.id)?.groupBy;
        });

        this.groupTasks();
        if (this.tasks.data) gantt.parse(this.tasks);
        this.addTodayMarker();
      },
      refreshGant: {
        handler: function (newValue, oldValue) {
          if (newValue != oldValue) {
            if (newValue == true) {
              this.refresh();
            }
          }
        },
        deep: true,
      },
      collectionSelected(val) {
        this.$emit('collectionSelected', val);
      },
      '$store.state.TASK.options': {
        handler() {
          // Reload gantt data when filters change
          this.loadGanttData();
        },
        deep: true
      },
      // routeToTaskFired: {
      //   handler: function (newValue, oldValue) {
      //
      //     if (newValue !== oldValue) {
      //       if (newValue) {
      //         if (Object.keys(this.$route.query).length > 0) {
      //           let isNew = false;
      //           let data = {
      //             task: { id: this.$route.query.taskId },
      //             isNew,
      //             taskTypeId: this.$route.params.taskTypeId,
      //           };
      //           setTimeout(() => {
      //             this.$refs.editTask.open(data);
      //             this.routeToTaskFired = false;
      //           }, 0);
      //         }
      //       }
      //     }
      //   },
      //   deep: true,
      // },
      $route: {
        deep: true,
        immediate: true,
        handler(route) {
          if (Object.keys(route.query).length > 0) {
            let isNew = false;
            let data = {
              task: { id: route.query.taskId },
              isNew,
              taskTypeId: route.params.taskTypeId,
            };
            setTimeout(() => {
              this.$refs.editTask.open(data);
              this.routeToTaskFired = false;
            }, 0);
          }
        },
      },
    },
    components: {
      EditTask,
      EditProject,
      EditContract,
      FacilitySelectDialog,
    },
  };
</script>

<style lang="scss">
  #gantt-container {
    font-family: 'Popins', sans-serif;
  }

  .gantt-dark-theme {
    .gantt_grid_scale,
    .gantt_row,
    .gantt_task_scale,
    .gantt_task_row {
      background: transparent;
      border-color: #555;
    }

    .gantt_layout_cell,
    .gantt_scale_cell,
    .gantt_task_row,
    .gantt_scale_line,
    .gantt_scale_cell {
      border-color: #555 !important;
    }

    .gantt_task_grid_row_resize {
      background: #555;
    }

    .gantt_tree_content,
    .gantt_grid_head_cell,
    .gantt_scale_cell {
      color: #fff !important;
    }

    .gantt_resizing {
      background: #555 !important;
    }
  }

  .gantt_task_line {
    background-color: #547d9a;
    border: 1px solid #547d9a;
  }

  button.task-action {
    background: transparent;
    border: none;
    font-size: 20px;
    cursor: pointer;
    color: var(--v-primary-darken1);

    &:hover {
      color: var(--v-primary-base);
    }
  }
  .status_line {
    background-color: #0ca30a;
  }

  .gantt_task_link.start_to_start .gantt_line_wrapper div {
    background-color: #dd5640;
  }

  .gantt_task_link.start_to_start:hover .gantt_line_wrapper div {
    box-shadow: 0 0 5px 0px #dd5640;
  }

  .gantt_task_link.start_to_start .gantt_link_arrow_right {
    border-left-color: #dd5640;
  }

  .gantt_task_link.finish_to_start .gantt_line_wrapper div {
    background-color: #7576ba;
  }

  .gantt_task_link.finish_to_start:hover .gantt_line_wrapper div {
    box-shadow: 0 0 5px 0px #7576ba;
  }

  .gantt_task_link.finish_to_start .gantt_link_arrow_right {
    border-left-color: #7576ba;
  }

  .gantt_task_link.finish_to_finish .gantt_line_wrapper div {
    background-color: #55d822;
  }

  .gantt_task_link.finish_to_finish:hover .gantt_line_wrapper div {
    box-shadow: 0 0 5px 0px #55d822;
  }

  .gantt_task_link.finish_to_finish .gantt_link_arrow_left {
    border-right-color: #55d822;
  }

  button.task-actions-menu {
    background: transparent;
    border: none;
    font-size: 20px;
    cursor: pointer;

    &:hover {
      color: var(--v-black-lighten2);
    }
  }
  .gantt-more-options {
    border-radius: 4px !important;
    z-index: 99;
  }

  .drag_date {
    color: #454545;
    font-size: 13px;
    text-align: center;
    z-index: 1;
  }

  .drag_date.drag_move_start {
    margin-left: -15px;
  }

  .drag_date.drag_move_end {
    margin-left: 15px;
  }

  .drag_move_vertical,
  .drag_move_horizontal {
    background-color: #9de19e;
    opacity: 0.7;
    box-sizing: border-box;
  }

  .drag_move_vertical {
    border-right: 1px #6ac666 solid;
    border-left: 1px #6ac666 solid;
  }

  .drag_move_horizontal {
    border-top: 1px #6ac666 solid;
    border-bottom: 1px #6ac666 solid;
  }

  .gantt_task_cell.week_end {
    background-color: #eff5fd;
  }

  .gantt-dark-theme .gantt_task_cell.week_end {
    background-color: #7fabe6;
  }
  .gantt_task_row.gantt_selected .gantt_task_cell.week_end {
    background-color: #f8ec9c;
  }
  .gantt_row_project .gantt_tree_content {
    font-weight: bold;
  }
  .task-grid-icon {
    align-self: center;
    margin-right: 2px;
  }
  .custom-project {
    position: absolute;
    height: 6px;
    color: #ffffff;
    background-color: #6c776b;
  }

  .custom-project div {
    position: absolute;
  }

  .project-left,
  .project-right {
    top: 6px;
    background-color: transparent;
    border-style: solid;
    width: 0px;
    height: 0px;
  }

  .project-left {
    left: 0px;
    border-width: 0px 0px 8px 7px;
    border-top-color: transparent;
    border-right-color: transparent !important;
    border-bottom-color: transparent !important;
    border-left-color: #607d8b !important;
  }

  .project-right {
    right: 0px;
    border-width: 0px 7px 8px 0px;
    border-top-color: transparent;
    border-right-color: #607d8b;
    border-bottom-color: transparent !important;
    border-left-color: transparent;
  }
  .toolbar-item {
    margin-bottom: 10px;
  }
  .gantt_task_row.gantt_selected .gantt_task_cell {
    background-color: rgba(0, 199, 181, 0.2);
  }
  .gantt_row:focus,
  .gantt_cell:focus,
  .gantt_btn_set:focus,
  .gantt_qi_big_icon:focus,
  .gantt_popup_button:focus,
  .gantt_grid_head_cell:focus {
    box-shadow: none;
  }

  .weekend {
    background: #c3dfff80;
  }
  .gantt-dark-theme .weekend {
    background: #9dc7f633;
  }

  .column_overload .gantt_histogram_fill {
    background-color: #ffa9a9;
  }

  .resource_marker {
    text-align: center;
  }

  .resource_marker div {
    width: 28px;
    height: 28px;
    line-height: 29px;
    display: inline-block;
    border-radius: 15px;
    color: #fff;
    margin: 3px;
  }

  .resource_marker.workday_ok div {
    background: #51c185;
  }

  .resource_marker.workday_over div {
    background: #ff8686;
    border-radius: 5px;
  }

  .resource-controls {
    margin: 0.5rem 1rem;
    display: flex;
  }

  :root {
    --form-control-color: #51ac9a;
  }

  .form-control {
    display: grid;
    grid-template-columns: 1em auto;
    gap: 0.5em;
  }

  .form-control:focus-within {
    color: var(--form-control-color);
  }

  input[type='radio'] {
    /* Add if not using autoprefixer */
    -webkit-appearance: none;
    /* Remove most all native input styles */
    appearance: none;
    /* For iOS < 15 */
    background-color: var(--form-background);
    /* Not removed via appearance */
    margin: 0;
    margin-top: 4px;
    font: inherit;
    color: currentColor;
    width: 1.15em;
    height: 1.15em;
    border: 0.15em solid currentColor;
    border-radius: 50%;
    transform: translateY(-0.075em);

    display: grid;
    place-content: center;
  }

  input[type='radio']::before {
    content: '';
    width: 0.65em;
    height: 0.65em;
    border-radius: 50%;
    transform: scale(0);
    transition: 120ms transform ease-in-out;
    box-shadow: inset 1em 1em var(--form-control-color);
    /* Windows High Contrast Mode */
    background-color: CanvasText;
  }

  input[type='radio']:checked::before {
    transform: scale(1);
  }

  input[type='radio']:focus {
    outline: max(2px, 0.1em) solid currentColor;
    // outline-offset: max(2px, 0.15em);
  }
  .form-control-wrapper {
    display: inline-block;
  }
  // .resources_cell {
  //   .gantt_task_cell {
  //     background-color: var(--v-bg7-base);
  //   }
  //   .gantt_task_cell.weekend {
  //     background-color: #d8e5f8;
  //   }

  //   .gantt_task_row.gantt_selected .gantt_task_cell.weekend {
  //     background-color: #f8ec9c;
  //   }
  //   // .gantt-dark-theme .gantt_task_cell {
  //   //   background-color: red;
  //   // }
  // }
  // .gantt-dark-theme {
  //   .resources_cell {
  //     .gantt_task_cell.weekend {
  //       background-color: #9dc7f633;
  //     }
  //   }
  // }
</style>
